import React from "react";
import {
  CalendarDays, Repeat, Users, UsersRound, MapPin, Package,
  Wallet, MessageSquare, ShieldCheck, ArrowRight, Sparkles, CheckCircle2,
} from "lucide-react";

const FEATURES = [
  { icon: CalendarDays, color: "from-blue-500 to-blue-600", title: "Agenda inteligente", text: "Visualize seus serviços por dia, semana ou mês. Cada limpeza aparece com horário, cliente, endereço e a equipe responsável. Você vê num piscar de olhos quantos serviços tem no dia e quem está livre." },
  { icon: Repeat, color: "from-indigo-500 to-indigo-600", title: "Agendamento recorrente", text: "Cadastre clientes semanais, quinzenais ou mensais e o sistema cria as próximas visitas automaticamente — sempre no mesmo dia da semana combinado. Nada de errar data ou esquecer de reagendar." },
  { icon: Users, color: "from-sky-500 to-sky-600", title: "Gestão de clientes", text: "Cadastro completo com endereços, contatos, histórico de visitas, preferências e observações. Um mapa mostra a localização de todos os clientes, ajudando a organizar rotas por região." },
  { icon: UsersRound, color: "from-emerald-500 to-emerald-600", title: "Equipes, colaboradores e rotas", text: "Monte equipes, defina cargos e distribua os serviços do dia. Acompanhe em tempo real quantas casas cada equipe já concluiu e quantas faltam." },
  { icon: MapPin, color: "from-teal-500 to-teal-600", title: "Mapa de Colaboradores", text: "Uma rede de profissionais de várias regiões cadastrados na plataforma. Quando você tem um serviço em determinada área, encontra rapidamente quem está disponível por perto." },
  { icon: Package, color: "from-amber-500 to-amber-600", title: "Controle de estoque", text: "Cadastre equipamentos e itens de limpeza, acompanhe o consumo e receba alertas quando algo estiver no nível mínimo — para nunca faltar material no meio do serviço." },
  { icon: Wallet, color: "from-violet-500 to-violet-600", title: "Financeiro completo", text: "Emita faturas (invoices), registre pagamentos e recebimentos e acompanhe a saúde financeira da empresa com relatórios claros de receita." },
  { icon: MessageSquare, color: "from-rose-500 to-rose-600", title: "Comunicação com o cliente", text: "Envie notificações e use modelos (templates) de mensagem para confirmar, lembrar e avisar seus clientes — com histórico de tudo o que foi enviado." },
  { icon: ShieldCheck, color: "from-slate-600 to-slate-700", title: "Segurança e controle", text: "Registro de atividades (audit log), gestão de dados da empresa e personalização visual do sistema com as cores da sua marca." },
];

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="features-page">
      {/* NAV */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="features-home">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-white shadow-sm shadow-blue-600/30">
              <Sparkles size={18} />
            </span>
            <span className="font-extrabold text-lg tracking-tight">Clean <span className="text-blue-600">Manager</span></span>
          </a>
          <a href="/signup" data-testid="features-nav-cta"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors">
            Começar grátis <ArrowRight size={16} />
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-white" />
        <div className="relative max-w-4xl mx-auto px-4 py-14 sm:py-20 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wide">
            Recursos
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
            Tudo o que a sua empresa de limpeza precisa, <span className="text-blue-600">em um só lugar</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            O Clean Manager USA reúne, numa única plataforma, tudo o que uma empresa de limpeza precisa
            para operar com organização e previsibilidade: da primeira visita agendada até o pagamento
            recebido. Chega de planilhas soltas, caderno e grupos de WhatsApp bagunçados.
          </p>
          <div className="mt-8">
            <a href="/signup" data-testid="features-hero-cta"
              className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold shadow-lg shadow-blue-600/25 transition-colors">
              Experimente grátis por 14 dias <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="max-w-6xl mx-auto px-4 pb-4 sm:pb-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title}`}
              className="rounded-2xl border border-slate-100 p-6 bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <span className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center text-white`}>
                <f.icon size={22} />
              </span>
              <h3 className="mt-4 font-bold text-lg">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-6xl mx-auto px-4 py-14 sm:py-20">
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-700 text-white p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10" />
          <div className="absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-white/10" />
          <div className="relative">
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">Comece a organizar sua empresa hoje</h2>
            <p className="mt-3 text-blue-100 max-w-xl mx-auto inline-flex items-center gap-2 justify-center">
              <CheckCircle2 size={18} /> Experimente grátis por 14 dias — sem cartão de crédito.
            </p>
            <div className="mt-7">
              <a href="/signup" data-testid="features-final-cta"
                className="inline-flex items-center gap-2 px-8 h-13 py-4 rounded-full bg-white text-blue-700 font-bold hover:bg-blue-50 transition-colors">
                Começar agora — é grátis <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
          <span className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-white"><Sparkles size={14} /></span>
            Clean Manager USA · {new Date().getFullYear()}
          </span>
          <div className="flex items-center gap-4">
            <a href="/" className="hover:text-blue-600">Voltar ao site</a>
            <a href="/signup" className="hover:text-blue-600 font-semibold">Começar grátis</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
